import { DottedPaths } from '@repo/types-client';

import { capitalize as stringCapitalize } from './string.helper';

/**
 * Gets the name of a property, optionally capitalizing it.
 * @function nameof
 * @template TObject - The type of the object.
 * @param {DottedPaths<TObject>} name - The name of the property.
 * @param {boolean} [capitalize=false] - Whether to capitalize the name.
 * @returns {string} The name of the property.
 */
const nameof = <TObject extends Record<string, any>>(
  name: DottedPaths<TObject>,
  capitalize = false,
): string => (capitalize ? stringCapitalize(<string>name) : <string>name);

/**
 * Sleeps for a specified number of seconds.
 * @function sleep
 * @param {number} [timeInSeconds=1] - The number of seconds to sleep.
 * @returns {Promise<void>} A promise that resolves after the specified time.
 */
const sleep = (timeInSeconds: number = 1) =>
  new Promise<void>((resolve) => setTimeout(resolve, timeInSeconds * 1000));

/**
 * Yields the thread to allow other tasks to execute.
 * @function yieldThread
 * @returns {Promise<void>} A promise that resolves immediately.
 */
const yieldThread = () =>
  new Promise<void>((resolve) => setTimeout(resolve, 0));

/**
 * Deeply merges two objects.
 * @function deepMerge
 * @template T - The type of the source object.
 * @param {T} source - The source object.
 * @param {object} target - The target object.
 * @returns {T} The merged object.
 */
const deepMerge = <T>(source: T, target: object): T => {
  const isObject = (item: unknown) =>
    item && typeof item === 'object' && !Array.isArray(item);

  const execute = (target: any, ...sources: any[]): T => {
    if (!sources.length) return target;

    const source = sources.shift();

    if (isObject(target) && isObject(source)) {
      for (const key in source) {
        if (isObject(source[key])) {
          if (!target[key]) Object.assign(target, { [key]: {} });

          execute(target[key], source[key]);
        } else {
          Object.assign(target, { [key]: source[key] });
        }
      }
    }

    return execute(target, ...sources);
  };

  return execute(source, target);
};

/**
 * Shuffles an array.
 * @function shuffle
 * @template T - The type of the elements in the array.
 * @param {Array<T>} iterable - The array to shuffle.
 * @returns {Array<T>} The shuffled array.
 */
const shuffle = <T>(iterable: T[]): T[] => {
  const array = [...iterable];

  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j] as T, array[i] as T];
  }

  return array;
};

/**
 * Checks if a value is not null or undefined.
 * @function hasValue
 * @template T - The type of the value.
 * @param {T} value - The value to check.
 * @returns {boolean} True if the value is not null or undefined, false otherwise.
 */
const hasValue = <T = unknown>(
  value: T,
): value is Exclude<T, null | undefined> =>
  ![null, undefined].includes(<any>value);

/**
 * Executes a callback with the window object if it exists.
 * @function getWindow
 * @template T - The return type of the callback.
 * @param {function(Window): T} callback - The callback to execute.
 * @returns {T | undefined} The result of the callback or undefined if the window object does not exist.
 */
const getWindow = <T>(callback: (window: Window) => T): T | undefined => {
  if (!window) return;
  return callback(window);
};

/**
 * Executes a callback with the document object if it exists.
 * @function getDocument
 * @template T - The return type of the callback.
 * @param {function(Document): T} callback - The callback to execute.
 * @returns {T | undefined} The result of the callback or undefined if the document object does not exist.
 */
const getDocument = <T>(callback: (document: Document) => T): T | undefined => {
  if (!document) return;
  return callback(document);
};

/**
 * Executes a callback with the navigator object if it exists.
 * @function getNavigator
 * @template T - The return type of the callback.
 * @param {function(Navigator): T} callback - The callback to execute.
 * @returns {T | undefined} The result of the callback or undefined if the navigator object does not exist.
 */
const getNavigator = <T>(
  callback: (navigator: Navigator) => T,
): T | undefined => {
  if (!document) return;
  return callback(navigator);
};

/**
 * Executes a callback with the localStorage object if it exists.
 * @function getLocalStorage
 * @template T - The return type of the callback.
 * @param {function(Storage): T} callback - The callback to execute.
 * @returns {T | undefined} The result of the callback or undefined if the localStorage object does not exist.
 */
const getLocalStorage = <T>(
  callback: (storage: Storage) => T,
): T | undefined => {
  if (!localStorage) return;
  return callback(localStorage);
};

/**
 * Sets an interval to execute an asynchronous callback.
 * @function setIntervalAsync
 * @param {function(): Promise<void>} callback - The asynchronous callback to execute.
 * @param {number} ms - The interval in milliseconds.
 * @returns {NodeJS.Timeout} The interval ID.
 */
const setIntervalAsync = (
  callback: () => Promise<void>,
  ms: number,
): NodeJS.Timeout => {
  const execute = async (
    callback: () => Promise<void>,
    timer: NodeJS.Timeout,
  ) => {
    await callback();
    timer.refresh();
  };

  const timer: NodeJS.Timeout = setTimeout(() => execute(callback, timer), ms);
  return timer;
};

export {
  getWindow,
  getLocalStorage,
  getNavigator,
  getDocument,
  hasValue,
  shuffle,
  nameof,
  sleep,
  deepMerge,
  yieldThread,
  setIntervalAsync,
};
